import { domToJpeg } from 'modern-screenshot';

/**
 * Screenshot capture options
 */
export interface ScreenshotOptions {
  /** CSS selector for elements to exclude from screenshot */
  excludeSelector?: string;
  /** JPEG quality (0-1), default: 0.7 */
  quality?: number;
  /** Max dimension in pixels, default: 1920 */
  maxDimension?: number;
}

const DEFAULT_OPTIONS: Required<ScreenshotOptions> = {
  excludeSelector: '[data-feedback-exclude]',
  quality: 0.5,
  maxDimension: 1600,
};

/**
 * Capture a screenshot of the current page
 * @returns Base64-encoded JPEG image string, or null if capture fails
 */
export async function captureScreenshot(
  options: ScreenshotOptions = {}
): Promise<string | null> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    console.warn('[FeedbackWidget] Screenshot capture is only available in browser environments');
    return null;
  }

  // Find and hide excluded elements
  const excludedElements = document.querySelectorAll(opts.excludeSelector);
  const originalVisibility: Map<Element, string> = new Map();

  excludedElements.forEach((el) => {
    const htmlEl = el as HTMLElement;
    originalVisibility.set(el, htmlEl.style.visibility);
    htmlEl.style.visibility = 'hidden';
  });

  try {
    // Calculate scale to fit within maxDimension
    const width = document.documentElement.scrollWidth;
    const height = document.documentElement.scrollHeight;
    const scale = Math.min(
      opts.maxDimension / width,
      opts.maxDimension / height,
      window.devicePixelRatio,
      2 // Cap at 2x for performance
    );

    // Capture the screenshot using modern-screenshot
    const dataUrl = await domToJpeg(document.body, {
      quality: opts.quality,
      scale,
      filter: (node: Node) => {
        // Exclude elements with data-feedback-exclude attribute
        if (node instanceof HTMLElement && node.hasAttribute('data-feedback-exclude')) {
          return false;
        }
        // Exclude cross-origin images to avoid CORS errors
        if (node instanceof HTMLImageElement) {
          const src = node.src;
          if (src && !src.startsWith(window.location.origin) && !src.startsWith('data:') && !src.startsWith('blob:')) {
            return false;
          }
        }
        return true;
      },
    });

    // Check size and reduce quality if too large (target under 100KB for API limits)
    let finalDataUrl = dataUrl;
    let sizeKB = Math.round((dataUrl.length * 3) / 4 / 1024);

    // If still too large, try lower quality
    if (sizeKB > 100) {
      console.debug(`[FeedbackWidget] Screenshot too large (${sizeKB}KB), reducing quality...`);
      const lowerQualityDataUrl = await domToJpeg(document.body, {
        quality: 0.3,
        scale: scale * 0.7,
        filter: (node: Node) => {
          if (node instanceof HTMLElement && node.hasAttribute('data-feedback-exclude')) {
            return false;
          }
          if (node instanceof HTMLImageElement) {
            const src = node.src;
            if (src && !src.startsWith(window.location.origin) && !src.startsWith('data:') && !src.startsWith('blob:')) {
              return false;
            }
          }
          return true;
        },
      });
      finalDataUrl = lowerQualityDataUrl;
      sizeKB = Math.round((finalDataUrl.length * 3) / 4 / 1024);
    }

    console.debug(`[FeedbackWidget] Screenshot captured: ${sizeKB}KB`);

    return finalDataUrl;
  } catch (error) {
    console.error('[FeedbackWidget] Screenshot capture failed:', error);
    return null;
  } finally {
    // Always restore excluded elements visibility, even if capture fails
    excludedElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.visibility = originalVisibility.get(el) || '';
    });
  }
}

/**
 * Check if screenshot capture is supported
 */
export function isScreenshotSupported(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}
